// Admin-gated dropdown to pick a workstation (and its associated warehouse) for a
// sales channel. Non-admins see a brief note; only admins can call the admin-only
// GET /v1/workstations endpoint.
//
// Badge reads warehouseName from localStorage (written by setWorkstation) so the
// cashier screen never needs to call the admin endpoint.

import { useQuery } from "@tanstack/react-query";
import { Settings2, WarehouseIcon } from "lucide-react";
import { apiGet } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useWorkstationConfig, type WorkstationChannel } from "./use-workstation-config";

interface Workstation {
  id: string;
  name: string;
  warehouseName: string;
  isActive: boolean;
}

interface Props {
  channel: WorkstationChannel;
  /** Called after a workstation is saved; callers can use to trigger re-mount effects. */
  onConfigured?: () => void;
}

export function WorkstationSelector({ channel, onConfigured }: Props) {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const config = useWorkstationConfig(channel);

  const { data: workstations = [], isLoading } = useQuery({
    queryKey: ["workstations"],
    queryFn: () => apiGet<Workstation[]>("/v1/workstations"),
    // Only fetch when the user is admin — endpoint is admin-only.
    enabled: isAdmin,
    staleTime: 60_000,
  });

  const activeWorkstations = workstations.filter((w) => w.isActive);

  // Non-admin: display a read-only notice (no fetch, no ability to change).
  if (!isAdmin) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
        Chỉ admin mới cấu hình kho bán.
      </div>
    );
  }

  const channelLabel = channel === "retail" ? "bán lẻ" : "bán sỉ";

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3 w-full max-w-sm">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Settings2 size={15} className="text-muted-foreground" />
        Cấu hình máy {channelLabel}
      </div>

      {/* Current config */}
      {config.isConfigured && (
        <div className="flex items-center gap-1.5 rounded-md bg-green-50 border border-green-200 px-2.5 py-1.5 text-xs dark:bg-green-950/30 dark:border-green-800">
          <WarehouseIcon size={12} className="text-green-600 dark:text-green-400 shrink-0" />
          <span className="text-green-700 dark:text-green-300 font-medium">
            {config.workstationName}
          </span>
          <span className="text-green-600 dark:text-green-400">
            — {config.warehouseName}
          </span>
        </div>
      )}

      {/* Selector */}
      <div className="space-y-1.5">
        <Label htmlFor={`ws-select-${channel}`} className="text-xs text-muted-foreground uppercase tracking-wide">
          {config.isConfigured ? "Đổi máy" : "Chọn máy"}
        </Label>
        {isLoading ? (
          <div className="h-9 rounded-md border bg-muted animate-pulse" />
        ) : (
          <select
            id={`ws-select-${channel}`}
            defaultValue=""
            onChange={(e) => {
              const selected = activeWorkstations.find((w) => w.id === e.target.value);
              if (!selected) return;
              config.setWorkstation({
                id: selected.id,
                name: selected.name,
                warehouseName: selected.warehouseName,
              });
              onConfigured?.();
              // Reset select to placeholder after saving
              e.target.value = "";
            }}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">— Chọn quầy bán —</option>
            {activeWorkstations.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — Kho: {w.warehouseName}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Clear button only shown when configured */}
      {config.isConfigured && (
        <Button
          variant="outline"
          size="sm"
          onClick={config.clear}
          className="w-full text-xs text-muted-foreground"
        >
          Xóa cấu hình máy này
        </Button>
      )}
    </div>
  );
}
